import { useEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { toast } from 'sonner';
import {
  ChevronLeft,
  ChevronRight,
  LayoutGrid,
  Save,
  CheckCircle2,
  Circle,
  LogOut,
  Moon,
  Sun,
  User,
  ChevronDown,
} from 'lucide-react';
import { useSession } from '../session';
import { api, type ApiError } from '../api';
import { isComplete } from '../../shared/scoring';
import type { Presentation, Score } from '../../shared/types';
import { ScoreCard, type Draft } from '../components/ScoreCard';
import { resolveCriteria } from '../../shared/rubrics';
import { BrandMark } from '../components/Brand';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { Skeleton } from '../components/ui/skeleton';
import {
  Sheet,
  SheetContent,
  SheetTitle,
  SheetTrigger,
  SheetClose,
} from '../components/ui/sheet';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { useTheme } from '../lib/theme';
import { cn } from '../lib/utils';

/** Orals first (by time slot), then posters (by board number). */
function orderQueue(items: Presentation[]): Presentation[] {
  const orals = items
    .filter((p) => p.type === 'oral')
    .sort((a, b) => (a.timeSlot || '').localeCompare(b.timeSlot || '') || a.id.localeCompare(b.id));
  const posters = items
    .filter((p) => p.type === 'poster')
    .sort((a, b) => (Number(a.boardNumber) || 0) - (Number(b.boardNumber) || 0) || a.id.localeCompare(b.id));
  return [...orals, ...posters];
}

export default function ScoringDeck() {
  const { judge, allocations, scores, loading, applyScore, rubrics } = useSession();

  const queue = useMemo(() => orderQueue(allocations), [allocations]);
  const scoredIds = useMemo(() => new Set(scores.map((s) => s.presentationId)), [scores]);
  const scoreByPres = useMemo(() => new Map(scores.map((s) => [s.presentationId, s])), [scores]);

  const [index, setIndex] = useState(0);
  const [direction, setDirection] = useState(0);
  const [drafts, setDrafts] = useState<Record<string, Draft>>({});
  const [saving, setSaving] = useState(false);
  const initialised = useRef(false);

  const completed = queue.filter((p) => scoredIds.has(p.id)).length;
  const total = queue.length;
  const current = queue[index];

  // Land on the first unscored item once data is ready.
  useEffect(() => {
    if (initialised.current || loading || total === 0) return;
    const firstUnscored = queue.findIndex((p) => !scoredIds.has(p.id));
    setIndex(firstUnscored === -1 ? 0 : firstUnscored);
    initialised.current = true;
  }, [loading, total, queue, scoredIds]);

  function deriveDraft(id: string): Draft {
    const s = scoreByPres.get(id);
    return s ? { values: { ...s.scores }, comments: s.comments } : { values: {}, comments: '' };
  }
  function draftFor(id: string): Draft {
    return drafts[id] ?? deriveDraft(id);
  }
  function updateDraft(id: string, fn: (d: Draft) => Draft) {
    setDrafts((prev) => ({ ...prev, [id]: fn(prev[id] ?? deriveDraft(id)) }));
  }

  function go(to: number, dir: number) {
    if (to < 0 || to >= total) return;
    setDirection(dir);
    setIndex(to);
  }
  const next = () => go(index + 1, 1);
  const prev = () => go(index - 1, -1);

  async function saveAndNext() {
    if (!current) return;
    const crit = resolveCriteria(rubrics, current.category, current.type);
    const d = draftFor(current.id);
    if (!isComplete(crit, d.values)) {
      toast.error('Please score every criterion before saving.');
      return;
    }
    setSaving(true);
    try {
      const saved = await api<Score>('save-score', {
        body: { presentationId: current.id, scores: d.values, comments: d.comments },
      });
      applyScore(saved);
      toast.success('Score saved');
      if (index < total - 1) next();
    } catch (err) {
      toast.error((err as ApiError).message || 'Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  }

  // Keyboard navigation (ignore when typing in a field).
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === 'TEXTAREA' || tag === 'INPUT') return;
      if (e.key === 'ArrowRight') next();
      if (e.key === 'ArrowLeft') prev();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  if (loading && total === 0) return <DeckSkeleton />;
  if (total === 0) {
    return (
      <div className="mx-auto flex min-h-dvh max-w-2xl flex-col items-center justify-center gap-3 p-6 text-center">
        <BrandMark className="size-14" />
        <h1 className="text-xl font-bold">No allocations yet</h1>
        <p className="text-muted-foreground">You have no presentations allocated. Please contact the organisers.</p>
      </div>
    );
  }

  const allDone = completed === total;

  return (
    <div className="mx-auto flex min-h-dvh max-w-2xl flex-col">
      {/* header */}
      <header className="sticky top-0 z-10 border-b bg-background/85 px-4 py-3 backdrop-blur">
        <div className="flex items-center justify-between gap-3">
          <BrandMark className="size-9 ring-1 ring-border" />
          <div className="min-w-0 flex-1">
            <div className="mb-1 flex items-center justify-between text-xs font-medium text-muted-foreground">
              <span>Scoring progress</span>
              <span className="tabular-nums">
                {completed}/{total}
              </span>
            </div>
            <Progress value={completed} max={total} className="h-1.5" />
          </div>
          <DeckMenu judgeName={judge?.name} category={judge?.category} completed={completed} total={total} />
        </div>
      </header>

      {/* deck */}
      <main className="relative flex-1 overflow-hidden px-4 pb-28 pt-5">
        <AnimatePresence mode="popLayout" custom={direction} initial={false}>
          <motion.div
            key={current.id}
            custom={direction}
            variants={{
              enter: (d: number) => ({ x: d > 0 ? 48 : -48, opacity: 0 }),
              center: { x: 0, opacity: 1 },
              exit: (d: number) => ({ x: d > 0 ? -48 : 48, opacity: 0 }),
            }}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.25, ease: 'easeOut' }}
            drag="x"
            dragConstraints={{ left: 0, right: 0 }}
            dragElastic={0.18}
            onDragEnd={(_e, info) => {
              if (info.offset.x < -80) next();
              else if (info.offset.x > 80) prev();
            }}
          >
            <ScoreCard
              presentation={current}
              criteria={resolveCriteria(rubrics, current.category, current.type)}
              draft={draftFor(current.id)}
              onSetScore={(critId, value) =>
                updateDraft(current.id, (dd) => ({ ...dd, values: { ...dd.values, [critId]: value } }))
              }
              onSetComments={(text) => updateDraft(current.id, (dd) => ({ ...dd, comments: text }))}
            />
          </motion.div>
        </AnimatePresence>
      </main>

      {/* footer controls */}
      <footer className="fixed inset-x-0 bottom-0 z-10 mx-auto max-w-2xl border-t bg-card/95 px-4 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] backdrop-blur">
        <div className="flex items-center gap-3">
          <Button variant="outline" size="icon" onClick={prev} disabled={index === 0} aria-label="Previous">
            <ChevronLeft />
          </Button>

          <QueueSheet
            queue={queue}
            index={index}
            scoredIds={scoredIds}
            scoreByPres={scoreByPres}
            onJump={(i) => go(i, i > index ? 1 : -1)}
          />

          <Button className="flex-1" onClick={saveAndNext} disabled={saving}>
            <Save />
            {saving ? 'Saving…' : index < total - 1 ? 'Save & Next' : allDone ? 'Save' : 'Save & Finish'}
          </Button>

          <Button variant="outline" size="icon" onClick={next} disabled={index === total - 1} aria-label="Next">
            <ChevronRight />
          </Button>
        </div>
      </footer>
    </div>
  );
}

function DeckMenu({
  judgeName,
  category,
  completed,
  total,
}: {
  judgeName?: string;
  category?: string;
  completed: number;
  total: number;
}) {
  const { logout } = useSession();
  const { dark, toggle } = useTheme();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" className="gap-1.5 px-2">
          <span className="flex size-7 items-center justify-center rounded-full bg-primary/12 text-xs font-bold text-primary">
            {judgeName?.[0] ?? '?'}
          </span>
          <ChevronDown className="size-4 text-muted-foreground" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-60">
        <DropdownMenuLabel className="flex items-center gap-2">
          <User className="size-4 text-muted-foreground" />
          <div>
            <div>{judgeName}</div>
            <div className="text-xs font-normal text-muted-foreground">{category} Judge</div>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <div className="px-2.5 py-1.5 text-sm text-muted-foreground">
          <span className="font-semibold text-foreground tabular-nums">
            {completed}/{total}
          </span>{' '}
          scored
        </div>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={(e) => { e.preventDefault(); toggle(); }}>
          {dark ? <Sun /> : <Moon />}
          {dark ? 'Light mode' : 'Dark mode'}
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => logout()} className="text-destructive focus:text-destructive">
          <LogOut />
          Logout
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function QueueSheet({
  queue,
  index,
  scoredIds,
  scoreByPres,
  onJump,
}: {
  queue: Presentation[];
  index: number;
  scoredIds: Set<string>;
  scoreByPres: Map<string, Score>;
  onJump: (i: number) => void;
}) {
  const orals = queue.map((p, i) => ({ p, i })).filter((x) => x.p.type === 'oral');
  const posters = queue.map((p, i) => ({ p, i })).filter((x) => x.p.type === 'poster');

  const Row = ({ p, i }: { p: Presentation; i: number }) => {
    const score = scoreByPres.get(p.id);
    return (
      <SheetClose asChild>
        <button
          onClick={() => onJump(i)}
          className={cn(
            'flex w-full items-center gap-3 rounded-lg border p-3 text-left transition-colors hover:bg-accent',
            i === index ? 'border-primary bg-accent' : 'bg-card',
          )}
        >
          {scoredIds.has(p.id) ? (
            <CheckCircle2 className="size-5 shrink-0 text-success" />
          ) : (
            <Circle className="size-5 shrink-0 text-muted-foreground/40" />
          )}
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <span className="font-semibold">{p.code ?? p.id}</span>
              {score && (
                <span className="font-semibold text-success">
                  {score.totalScore}/{score.maxScore}
                </span>
              )}
            </div>
            <div className="line-clamp-1 text-sm font-medium">{p.title}</div>
          </div>
        </button>
      </SheetClose>
    );
  };

  return (
    <Sheet>
      <SheetTrigger asChild>
        <Button variant="outline" className="gap-2">
          <LayoutGrid className="size-4" />
          <span className="tabular-nums">
            {index + 1}/{queue.length}
          </span>
        </Button>
      </SheetTrigger>
      <SheetContent side="right" className="w-full max-w-sm">
        <SheetTitle className="px-4 pt-4 text-lg font-bold">Your queue</SheetTitle>
        <div className="flex-1 space-y-5 overflow-y-auto px-4 pb-6">
          {orals.length > 0 && (
            <div className="space-y-2">
              <div className="text-xs font-bold uppercase tracking-wide text-muted-foreground">
                Oral · {orals.length}
              </div>
              {orals.map((x) => (
                <Row key={x.p.id} p={x.p} i={x.i} />
              ))}
            </div>
          )}
          {posters.length > 0 && (
            <div className="space-y-2">
              <div className="text-xs font-bold uppercase tracking-wide text-muted-foreground">
                Posters · {posters.length}
              </div>
              {posters.map((x) => (
                <Row key={x.p.id} p={x.p} i={x.i} />
              ))}
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

function DeckSkeleton() {
  return (
    <div className="mx-auto max-w-2xl space-y-4 p-4">
      <Skeleton className="h-12 w-full" />
      <Skeleton className="h-24 w-full rounded-lg" />
      <Skeleton className="h-28 w-full rounded-lg" />
      <Skeleton className="h-28 w-full rounded-lg" />
    </div>
  );
}
